import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatTL } from "@/lib/format";
import { PageHeader, AddButton, Field, inputCls } from "@/components/shared";
import { usePermission } from "@/lib/permissions";
import { Trash2, Pencil, X, Landmark } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", bank_name: "", balance: "" };

export default function BankAccounts() {
  const canEdit = usePermission("bank_accounts", "edit");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const { data } = await api.get("/bank-accounts");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, balance: String(a.balance) }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, balance: parseFloat(form.balance) || 0 };
    try {
      if (editing) { await api.put(`/bank-accounts/${editing.id}`, payload); toast.success("Hesap güncellendi"); }
      else { await api.post("/bank-accounts", payload); toast.success("Hesap eklendi"); }
      setOpen(false); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu hesabı silmek istediğinize emin misiniz?")) return;
    await api.delete(`/bank-accounts/${id}`);
    toast.success("Silindi"); load();
  };

  const total = items.reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader
        eyebrow="Nakit varlıklar"
        title="Banka Hesapları"
        description="Tüm hesaplarınızı ve bakiyelerinizi yönetin."
        action={canEdit ? <AddButton onClick={openAdd} label="Yeni Hesap" testid="add-account-btn" /> : null}
      />

      <div className="mb-6 bg-gradient-to-br from-[#8A5A44] to-[#714835] text-white rounded-2xl p-6 md:p-8 relative overflow-hidden" data-testid="accounts-total-card">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-2 relative">Toplam Bakiye</p>
        <p className="font-heading text-4xl md:text-5xl relative">{formatTL(total)}</p>
        <p className="text-white/80 text-sm mt-1 relative">{items.length} hesap</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-[#E6E4DD] rounded-2xl p-12 text-center">
            <Landmark size={40} className="mx-auto mb-3 text-[#5C5B57]" strokeWidth={1.5} />
            <p className="text-[#5C5B57]">Henüz banka hesabı eklenmedi.</p>
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="bg-white border border-[#E6E4DD] rounded-2xl p-6 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-shadow" data-testid={`account-card-${a.id}`}>
            <div className="flex items-start justify-between mb-6">
              <div className="w-11 h-11 rounded-xl bg-[#F0EFEA] flex items-center justify-center">
                <Landmark size={20} strokeWidth={1.75} className="text-[#8A5A44]" />
              </div>
              {canEdit && (
              <div className="flex gap-1">
                <button onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`} className="p-2 rounded-lg hover:bg-[#F0EFEA] text-[#5C5B57]"><Pencil size={16} /></button>
                <button onClick={() => remove(a.id)} data-testid={`delete-account-${a.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><Trash2 size={16} /></button>
              </div>
              )}
            </div>
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5B57] mb-1">{a.bank_name}</p>
            <p className="font-heading text-xl text-[#2D2C2A] mb-4">{a.name}</p>
            <p className="font-heading text-2xl text-[#8A5A44]">{formatTL(a.balance)}</p>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="account-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl text-[#2D2C2A]">{editing ? "Hesap Düzenle" : "Yeni Hesap"}</h3>
              <button onClick={() => setOpen(false)} className="text-[#5C5B57]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Hesap Adı"><input required className={inputCls} data-testid="account-name-input" placeholder="Örn: Ana Hesap" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Banka"><input required className={inputCls} data-testid="account-bank-input" placeholder="Örn: Ziraat Bankası" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></Field>
              <Field label="Bakiye (₺)"><input required type="number" step="0.01" className={inputCls} data-testid="account-balance-input" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} /></Field>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 hover:bg-[#F0EFEA]">İptal</button>
                <button type="submit" data-testid="account-submit" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">{editing ? "Güncelle" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
